/* ==========================================================================
   TIIAN V2 — Global scripts (vanilla web components, no dependencies)
   ========================================================================== */

'use strict';

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/* Natív alert() felülírása márkás modállal ---------------------------------
   A Parcely app natív `alert()`-tel jelzi, ha nincs csomagpont választva.
   A saját appja kódját nem tudjuk módosítani, ezért a globális `alert`-et
   cseréljük le — az eredetit megtartjuk tartaléknak. */

const nativeAlert = window.alert.bind(window);
let themeAlertLastFocus = null;

function closeThemeAlert() {
  const existing = document.getElementById('ThemeAlert');
  if (!existing) return;
  existing.remove();
  document.documentElement.classList.remove('theme-alert-open');
  if (themeAlertLastFocus && themeAlertLastFocus.focus) themeAlertLastFocus.focus();
}

function themeAlert(message) {
  const text = String(message == null ? '' : message);
  const strings = window.themeStrings || {};

  try {
    closeThemeAlert();
    themeAlertLastFocus = document.activeElement;

    const root = document.createElement('div');
    root.id = 'ThemeAlert';
    root.className = 'theme-alert';
    root.innerHTML = `
      <div class="theme-alert__overlay" data-alert-close></div>
      <div class="theme-alert__panel" role="alertdialog" aria-modal="true" aria-labelledby="ThemeAlertTitle">
        <span class="theme-alert__icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <path d="M12 9v4M12 17h.01M10.3 3.9 1.8 18.1A2 2 0 0 0 3.5 21h17a2 2 0 0 0 1.7-2.9L13.7 3.9a2 2 0 0 0-3.4 0Z"/>
          </svg>
        </span>
        <h2 class="theme-alert__title" id="ThemeAlertTitle"></h2>
        <p class="theme-alert__message"></p>
        <div class="theme-alert__actions"></div>
      </div>`;

    root.querySelector('.theme-alert__title').textContent = strings.alertTitle || '';
    root.querySelector('.theme-alert__message').textContent = text;

    const actions = root.querySelector('.theme-alert__actions');
    const widget = document.getElementById('parcelyWidget');
    if (widget) {
      const jump = document.createElement('button');
      jump.type = 'button';
      jump.className = 'button';
      jump.textContent = strings.alertAction || '';
      jump.addEventListener('click', () => {
        closeThemeAlert();
        widget.scrollIntoView({ behavior: 'smooth', block: 'center' });
        widget.classList.add('is-highlighted');
        setTimeout(() => widget.classList.remove('is-highlighted'), 2400);
      });
      actions.appendChild(jump);
    }

    const dismiss = document.createElement('button');
    dismiss.type = 'button';
    dismiss.className = widget ? 'button button--tertiary' : 'button';
    dismiss.textContent = strings.alertDismiss || '';
    dismiss.setAttribute('data-alert-close', '');
    actions.appendChild(dismiss);

    root.addEventListener('click', (event) => {
      if (event.target.closest('[data-alert-close]')) closeThemeAlert();
    });

    document.body.appendChild(root);
    document.documentElement.classList.add('theme-alert-open');
    (actions.querySelector('button') || root).focus();
  } catch (error) {
    console.error('Theme alert failed', error);
    nativeAlert(text);
  }
}

/* themeConfirm — kétgombos, témába illő megerősítés ------------------------
   A `themeAlert` egygombos testvére: ugyanazt a panelt használja, de Promise-t
   ad vissza, és a megerősítés az egyetlen út a `true` felé. Minden más kiszállás
   (Mégsem, háttérre kattintás, Esc) `false`, mert itt a felhasználó tudomásul
   vételét rögzítjük — kétes helyzetben nem tekintjük megadottnak.

   Natív `confirm`-ra szándékosan nem esünk vissza: a `window.confirm`-ot a
   böngészők blokkolhatják, és a natív ablak kilóg az arculatból. Ha a panelt
   valamiért nem tudjuk felépíteni, inkább `false`-szal térünk vissza — a
   kosárba tétel elmarad, de nem történik megerősítés nélküli vásárlás. */
function themeConfirm(message, options) {
  const text = String(message == null ? '' : message);
  const strings = window.themeStrings || {};
  const config = options || {};

  return new Promise((resolve) => {
    let settled = false;
    const finish = (value) => {
      if (settled) return;
      settled = true;
      document.removeEventListener('keydown', onKeydown, true);
      closeThemeAlert();
      resolve(value);
    };

    function onKeydown(event) {
      if (event.key === 'Escape') finish(false);
    }

    try {
      closeThemeAlert();
      themeAlertLastFocus = document.activeElement;

      const root = document.createElement('div');
      root.id = 'ThemeAlert';
      root.className = 'theme-alert theme-alert--confirm';
      root.innerHTML = `
        <div class="theme-alert__overlay" data-confirm-cancel></div>
        <div class="theme-alert__panel" role="alertdialog" aria-modal="true" aria-labelledby="ThemeAlertTitle">
          <span class="theme-alert__icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
              <circle cx="12" cy="12" r="9"/>
              <path d="M12 6.5V12l3.5 2.5"/>
            </svg>
          </span>
          <h2 class="theme-alert__title" id="ThemeAlertTitle"></h2>
          <p class="theme-alert__message"></p>
          <div class="theme-alert__actions"></div>
        </div>`;

      root.querySelector('.theme-alert__title').textContent = config.title || strings.alertTitle || '';
      root.querySelector('.theme-alert__message').textContent = text;

      const actions = root.querySelector('.theme-alert__actions');

      const accept = document.createElement('button');
      accept.type = 'button';
      accept.className = 'button';
      accept.textContent = config.confirmLabel || strings.expiryConfirm || '';
      accept.addEventListener('click', () => finish(true));
      actions.appendChild(accept);

      const cancel = document.createElement('button');
      cancel.type = 'button';
      cancel.className = 'button button--tertiary';
      cancel.textContent = config.cancelLabel || strings.expiryCancel || '';
      cancel.setAttribute('data-confirm-cancel', '');
      actions.appendChild(cancel);

      root.addEventListener('click', (event) => {
        if (event.target.closest('[data-confirm-cancel]')) finish(false);
      });
      document.addEventListener('keydown', onKeydown, true);

      document.body.appendChild(root);
      document.documentElement.classList.add('theme-alert-open');
      accept.focus();
    } catch (error) {
      console.error('Theme confirm failed', error);
      finish(false);
    }
  });
}

window.alert = themeAlert;

/* Fizetés gomb őrzése -----------------------------------------------------
   A Parcely appnak van saját őrzése a fizetés gombon, de az némán elbukik:
   a kattintást megállítja, a hibaüzenetét viszont már nem jeleníti meg — a
   vásárló nulla visszajelzést kap, és nem érti, miért nem történik semmi.
   A pénztárba jutás integritását nem bízzuk külső appra: a téma ugyanazt az
   állapotot ellenőrzi (`window.PARCELY_APP`), capture fázisban, hogy biztosan
   lefusson. A szöveget az app saját, lokalizált feliratából vesszük át. */

// Csak digitális termékeket tartalmazó kosárhoz nem kell szállítási mód — a
// Parcely ilyenkor el sem rendereli a választót, így a `standartShippingSelected`
// örökre `null` maradna, és az őrzés sosem engedne tovább. A döntést Liquidben
// hozzuk meg (vendor alapján), a JS csak beolvassa.
function cartNeedsShipping() {
  const cart = document.querySelector('[data-needs-shipping]');
  if (!cart) return true; // ismeretlen helyzetben inkább őrzünk
  return cart.dataset.needsShipping !== 'false';
}

function parcelyBlockMessage() {
  // Ha nincs Parcely konténer az oldalon, nincs mit őriznünk.
  if (document.getElementById('parcelyWidget') === null) return null;

  // Digitális-only kosár: nincs mit választani, engedjük tovább.
  if (!cartNeedsShipping()) return null;

  const strings = window.themeStrings || {};
  const fallback = strings.alertShippingMethod || '';
  const app = window.PARCELY_APP;

  // A widget még tölt: zárva maradunk. Korábban itt átengedtünk, ezért a
  // betöltés előtt megnyomott gombbal ki lehetett kerülni az őrzést.
  if (!app || app.widgetLoaded !== true) {
    return strings.alertLoading || fallback;
  }

  // Nincs szállítási mód választva.
  if (app.standartShippingSelected === null || app.standartShippingSelected === undefined) {
    return app.selectMethodLabel || fallback;
  }
  // Csomagpontos mód van kiválasztva, de nincs konkrét pont.
  if (app.standartShippingSelected === false && !app.selectedLocation) {
    return app.selectLocationLabel || fallback;
  }
  return null;
}

/* A fizetés gomb letiltása, amíg a Parcely fel nem áll ----------------------
   A widget betöltése akár másodpercekig tarthat. Addig a gomb letiltottnak
   látszik, és egy rövid minimum-késleltetést is tartunk, hogy a gyorsan kattintó
   vásárló se csússzon át az őrzésen. */

const CHECKOUT_GATE_MIN_DELAY = 2500;
const CHECKOUT_GATE_POLL = 150;

function initCheckoutGate() {
  const widget = document.getElementById('parcelyWidget');
  const button = document.querySelector('[name="checkout"]');
  if (!widget || !button) return;

  // Digitális-only kosárnál a widget sosem áll fel — a kaput ki kell hagyni,
  // különben a gomb tartósan letiltva maradna.
  if (!cartNeedsShipping()) return;

  const startedAt = Date.now();
  button.setAttribute('aria-disabled', 'true');

  const timer = window.setInterval(() => {
    const app = window.PARCELY_APP;
    const ready = !!(app && app.widgetLoaded === true);
    if (!ready || Date.now() - startedAt < CHECKOUT_GATE_MIN_DELAY) return;
    window.clearInterval(timer);
    button.removeAttribute('aria-disabled');
  }, CHECKOUT_GATE_POLL);
}

initCheckoutGate();

document.addEventListener(
  'click',
  (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const button = target ? target.closest('[name="checkout"]') : null;
    if (!button) return;

    const message = parcelyBlockMessage();
    if (!message) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    themeAlert(message);
  },
  true
);

document.addEventListener('keydown', (event) => {
  if (event.key === 'Escape') closeThemeAlert();
});

/* Véletlenszerű termékrács (kiemelt kollekció "random" mód) ----------------- */

document.querySelectorAll('[data-random-grid]').forEach((grid) => {
  const items = Array.from(grid.children);
  for (let i = items.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [items[i], items[j]] = [items[j], items[i]];
  }
  const visibleCount = Number(grid.dataset.visibleCount) || items.length;
  items.forEach((item, index) => {
    grid.appendChild(item);
    item.hidden = index >= visibleCount;
  });
});

/* Drawer handling (menu drawer, cart drawer) ------------------------------- */

function openDrawer(drawer) {
  drawer.classList.add('active');
  document.body.classList.add('overflow-hidden');
  const focusable = drawer.querySelector('button, a, input, select, textarea');
  if (focusable) focusable.focus({ preventScroll: true });
}

function closeDrawer(drawer) {
  drawer.classList.remove('active');
  if (!document.querySelector('.drawer.active')) {
    document.body.classList.remove('overflow-hidden');
  }
}

document.addEventListener('click', (event) => {
  const opener = event.target.closest('[data-drawer-open]');
  if (opener) {
    const drawer = document.getElementById(opener.dataset.drawerOpen);
    if (drawer) {
      event.preventDefault();
      openDrawer(drawer);
    }
    return;
  }
  const closer = event.target.closest('[data-drawer-close]');
  if (closer) {
    const drawer = closer.closest('.drawer');
    if (drawer) closeDrawer(drawer);
  }
});

document.addEventListener('keyup', (event) => {
  if (event.key !== 'Escape') return;
  document.querySelectorAll('.drawer.active').forEach(closeDrawer);
  document.querySelectorAll('details-modal details[open]').forEach((details) => {
    details.removeAttribute('open');
  });
});

/* <header-component> — sticky header shadow -------------------------------- */

class HeaderComponent extends HTMLElement {
  connectedCallback() {
    this.onScroll = () => {
      this.style.boxShadow = window.scrollY > 10 ? '0 10px 30px rgba(0,0,0,0.35)' : 'none';
    };
    window.addEventListener('scroll', this.onScroll, { passive: true });
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
  }
}
customElements.define('header-component', HeaderComponent);

/* <details-modal> — search modal ------------------------------------------- */

class DetailsModal extends HTMLElement {
  connectedCallback() {
    this.details = this.querySelector('details');
    if (!this.details) return;
    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-modal-close]')) {
        this.details.removeAttribute('open');
        return;
      }
    });
    this.details.addEventListener('toggle', () => {
      if (this.details.open) {
        const input = this.details.querySelector('input[type="search"]');
        if (input) input.focus();
      }
    });
  }
}
customElements.define('details-modal', DetailsModal);

/* <details-disclosure> — close on outside click ---------------------------- */

class DetailsDisclosure extends HTMLElement {
  connectedCallback() {
    this.details = this.querySelector('details');
    if (!this.details) return;
    document.addEventListener('click', (event) => {
      if (this.details.open && !this.contains(event.target)) {
        this.details.removeAttribute('open');
      }
    });
  }
}
customElements.define('details-disclosure', DetailsDisclosure);

/* <quantity-input> ---------------------------------------------------------- */

class QuantityInput extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input');
    this.addEventListener('click', (event) => {
      const button = event.target.closest('button');
      if (!button) return;
      event.preventDefault();
      const previousValue = this.input.value;
      if (button.name === 'plus') this.input.stepUp();
      if (button.name === 'minus') this.input.stepDown();
      if (previousValue !== this.input.value) {
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }
}
customElements.define('quantity-input', QuantityInput);

/* Cart helpers -------------------------------------------------------------- */

// A Section Rendering API a szekció ID-jét várja (JSON sablonban a kulcsot,
// pl. "main"), nem a típusát ("main-cart") — ezért a DOM-ból olvassuk ki.
function cartSectionIds() {
  const ids = [];
  document.querySelectorAll('[data-cart-section-id]').forEach((el) => {
    const id = el.dataset.cartSectionId;
    if (id && ids.indexOf(id) === -1) ids.push(id);
  });
  return ids;
}

async function refreshCartSections() {
  const sectionIds = cartSectionIds();
  if (sectionIds.length === 0) return {};
  const url = `${window.themeRoutes.cart_url}?sections=${sectionIds.join(',')}&t=${Date.now()}`;
  const response = await fetch(url);
  return response.json();
}

function replaceCartSection(sections, selector) {
  const current = document.querySelector(selector);
  if (!current) return null;
  const html = sections[current.dataset.cartSectionId];
  if (!html) return null;
  const parsed = new DOMParser().parseFromString(html, 'text/html');
  const next = parsed.querySelector(selector);
  if (!next) return null;
  current.replaceWith(next);
  return next;
}

function replaceCartDrawer(sections) {
  const current = document.getElementById('CartDrawer');
  if (!current) return;
  const wasActive = current.classList.contains('active');
  const next = replaceCartSection(sections, 'cart-drawer');
  if (next && wasActive) next.classList.add('active');
}

function updateCartCount(count) {
  document.querySelectorAll('[data-cart-count]').forEach((bubble) => {
    bubble.textContent = count;
    bubble.classList.toggle('hidden', count === 0);
  });
}

// A Parcely a szállításimód-választót a #parcelyWidget konténerbe injektálja,
// és a fizetés gombot / kosár űrlapot saját eseménykezelővel őrzi. Mindkettő a
// <cart-items> szekción BELÜL van, így a szekció AJAX cseréje megsemmisítené
// őket — a gomb utána őrzés nélkül vinne a checkoutra. Ha a widget jelen van,
// ezért teljes újratöltéssel frissítünk.
function cartHasThirdPartyWidget() {
  return document.getElementById('parcelyWidget') !== null;
}

async function updateCartUI(openTheDrawer = false) {
  if (cartHasThirdPartyWidget()) {
    window.location.reload();
    return;
  }

  try {
    const [sections, cart] = await Promise.all([
      refreshCartSections(),
      fetch(`${window.themeRoutes.cart_url}.js`).then((r) => r.json()),
    ]);
    replaceCartDrawer(sections);
    replaceCartSection(sections, 'cart-items');
    replaceCartSection(sections, 'cart-promo');
    updateCartCount(cart.item_count);
    if (openTheDrawer) {
      const drawer = document.getElementById('CartDrawer');
      if (drawer) openDrawer(drawer);
    }
  } catch (error) {
    console.error('Cart refresh failed', error);
  }
}

async function changeCartLine(line, quantity, errorTarget) {
  try {
    const response = await fetch(window.themeRoutes.cart_change_url, {
      ...fetchConfig(),
      body: JSON.stringify({ line, quantity }),
    });
    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.description || window.themeStrings.cartError);
    }
    await updateCartUI(false);
  } catch (error) {
    if (errorTarget) {
      errorTarget.textContent = error.message || window.themeStrings.cartError;
      errorTarget.hidden = false;
    }
    await updateCartUI(false);
  }
}

/* <cart-drawer> ------------------------------------------------------------- */

class CartDrawer extends HTMLElement {
  connectedCallback() {
    this.addEventListener('change', (event) => {
      const input = event.target.closest('.quantity__input');
      if (!input) return;
      const line = Number(input.dataset.index);
      changeCartLine(line, Number(input.value), this.querySelector('[data-cart-error]'));
    });
    this.addEventListener('click', (event) => {
      const removeButton = event.target.closest('[data-remove-line]');
      if (!removeButton) return;
      event.preventDefault();
      changeCartLine(Number(removeButton.dataset.removeLine), 0, this.querySelector('[data-cart-error]'));
    });
  }
}
customElements.define('cart-drawer', CartDrawer);

/* <cart-items> — cart page -------------------------------------------------- */

class CartItems extends HTMLElement {
  connectedCallback() {
    this.addEventListener('change', (event) => {
      const input = event.target.closest('.quantity__input');
      if (input) {
        changeCartLine(Number(input.dataset.index), Number(input.value), this.querySelector('[data-cart-error]'));
        return;
      }
      if (event.target.id === 'CartNote') {
        fetch(window.themeRoutes.cart_update_url, {
          ...fetchConfig(),
          body: JSON.stringify({ note: event.target.value }),
        });
      }
    });
    this.addEventListener('click', (event) => {
      const removeButton = event.target.closest('[data-remove-line]');
      if (!removeButton) return;
      event.preventDefault();
      changeCartLine(Number(removeButton.dataset.removeLine), 0, this.querySelector('[data-cart-error]'));
    });
  }
}
customElements.define('cart-items', CartItems);

/* Egyedi legördülők — minden natív <select> brand-skint kap ------------------ */

(() => {
  const CHEVRON =
    '<svg class="custom-select__chevron" viewBox="0 0 10 6" aria-hidden="true" focusable="false"><path d="M1 1l4 4 4-4" fill="none" stroke="currentColor" stroke-width="1.2"/></svg>';

  const SKIP_CONTAINERS = '#parcelyWidget, #parcelyWidgetModal, .filter-modal_overlay, [data-no-custom-select]';

  class CustomSelect {
    constructor(select) {
      this.select = select;
      select.dataset.customSelect = 'true';

      this.wrapper = document.createElement('div');
      this.wrapper.className = 'custom-select';

      this.trigger = document.createElement('button');
      this.trigger.type = 'button';
      // A natív select osztályait a gomb is megkapja, így a kontextus-függő
      // stílusok (pl. .localization__select) továbbra is érvényesülnek.
      this.trigger.className = `custom-select__trigger ${select.className}`.trim();
      this.trigger.setAttribute('aria-haspopup', 'listbox');
      this.trigger.setAttribute('aria-expanded', 'false');
      const label = select.id ? document.querySelector(`label[for="${CSS.escape(select.id)}"]`) : null;
      if (label) this.trigger.setAttribute('aria-label', label.textContent.trim());

      this.valueSpan = document.createElement('span');
      this.valueSpan.className = 'custom-select__value';
      this.trigger.append(this.valueSpan);
      this.trigger.insertAdjacentHTML('beforeend', CHEVRON);

      this.list = document.createElement('ul');
      this.list.className = 'custom-select__list';
      this.list.setAttribute('role', 'listbox');
      this.list.tabIndex = -1;

      select.parentNode.insertBefore(this.wrapper, select);
      this.wrapper.append(select, this.trigger, this.list);
      select.classList.add('custom-select__native');
      select.setAttribute('tabindex', '-1');
      select.setAttribute('aria-hidden', 'true');

      this.activeIndex = -1;
      this.updateLabel();

      this.trigger.addEventListener('click', () => (this.isOpen() ? this.close() : this.open()));
      this.trigger.addEventListener('keydown', (event) => {
        if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) {
          event.preventDefault();
          this.open();
        }
      });
      this.list.addEventListener('keydown', (event) => this.onListKeydown(event));
      this.list.addEventListener('click', (event) => {
        const item = event.target.closest('[role="option"]');
        if (item && item.getAttribute('aria-disabled') !== 'true') {
          this.commit(Number(item.dataset.index));
        }
      });
      this.onOutside = (event) => {
        if (this.isOpen() && !this.wrapper.contains(event.target)) this.close(false);
      };
      document.addEventListener('click', this.onOutside);
      select.addEventListener('change', () => this.updateLabel());
    }

    isOpen() {
      return this.wrapper.classList.contains('is-open');
    }

    updateLabel() {
      const option = this.select.options[this.select.selectedIndex];
      this.valueSpan.textContent = option ? option.textContent.trim() : '';
    }

    /* A listát nyitáskor építjük újra, így a dinamikusan cserélt opciók
       (pl. Shopify országlista) mindig frissek. */
    renderList() {
      this.list.innerHTML = '';
      Array.from(this.select.options).forEach((option, index) => {
        const group = option.parentElement;
        if (group.tagName === 'OPTGROUP' && group.firstElementChild === option) {
          const groupItem = document.createElement('li');
          groupItem.className = 'custom-select__group';
          groupItem.textContent = group.label;
          this.list.append(groupItem);
        }
        const item = document.createElement('li');
        item.className = 'custom-select__option';
        item.setAttribute('role', 'option');
        item.dataset.index = String(index);
        item.textContent = option.textContent.trim();
        if (option.disabled) item.setAttribute('aria-disabled', 'true');
        if (index === this.select.selectedIndex) item.setAttribute('aria-selected', 'true');
        this.list.append(item);
      });
    }

    open() {
      this.updateLabel();
      this.renderList();
      this.wrapper.classList.add('is-open');
      this.trigger.setAttribute('aria-expanded', 'true');
      // Ha alul nincs hely, felfelé nyíljon
      const rect = this.trigger.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      this.wrapper.classList.toggle('custom-select--up', spaceBelow < 300 && rect.top > spaceBelow);
      this.setActive(this.select.selectedIndex, false);
      this.list.focus({ preventScroll: true });
      this.scrollToActive();
    }

    close(refocus = true) {
      this.wrapper.classList.remove('is-open');
      this.trigger.setAttribute('aria-expanded', 'false');
      if (refocus) this.trigger.focus({ preventScroll: true });
    }

    options() {
      return Array.from(this.list.querySelectorAll('[role="option"]'));
    }

    setActive(index, scroll = true) {
      const items = this.options();
      this.activeIndex = Math.max(0, Math.min(index, items.length - 1));
      items.forEach((item, i) => item.classList.toggle('is-active', i === this.activeIndex));
      if (scroll) this.scrollToActive();
    }

    scrollToActive() {
      const item = this.options()[this.activeIndex];
      if (item) item.scrollIntoView({ block: 'nearest' });
    }

    moveActive(delta) {
      const items = this.options();
      let next = this.activeIndex;
      do {
        next += delta;
      } while (items[next] && items[next].getAttribute('aria-disabled') === 'true');
      if (items[next]) this.setActive(next);
    }

    onListKeydown(event) {
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          this.moveActive(1);
          break;
        case 'ArrowUp':
          event.preventDefault();
          this.moveActive(-1);
          break;
        case 'Home':
          event.preventDefault();
          this.setActive(0);
          break;
        case 'End':
          event.preventDefault();
          this.setActive(this.options().length - 1);
          break;
        case 'Enter':
        case ' ':
          event.preventDefault();
          this.commit(this.activeIndex);
          break;
        case 'Escape':
          event.preventDefault();
          this.close();
          break;
        case 'Tab':
          this.close(false);
          break;
        default:
          if (event.key.length === 1 && /\S/.test(event.key)) {
            const query = event.key.toLowerCase();
            const items = this.options();
            const start = this.activeIndex + 1;
            for (let i = 0; i < items.length; i++) {
              const item = items[(start + i) % items.length];
              if (item.textContent.trim().toLowerCase().startsWith(query)) {
                this.setActive(Number(item.dataset.index));
                break;
              }
            }
          }
      }
    }

    commit(index) {
      if (index >= 0 && index !== this.select.selectedIndex) {
        this.select.selectedIndex = index;
        // Bubbling change: az inline onchange-ek (form.submit) is lefutnak
        this.select.dispatchEvent(new Event('change', { bubbles: true }));
      }
      this.updateLabel();
      this.close();
    }
  }

  function initCustomSelects(root) {
    const scope = root.querySelectorAll ? root : document;
    scope.querySelectorAll('select:not([multiple]):not([data-custom-select])').forEach((select) => {
      if (select.size > 1 || select.closest(SKIP_CONTAINERS)) return;
      new CustomSelect(select);
    });
  }

  window.initCustomSelects = initCustomSelects;
  initCustomSelects(document);

  // Dinamikusan érkező selectek (kosár-újrarender, szerkesztő, appok)
  new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType !== Node.ELEMENT_NODE) continue;
        if (node.matches && node.matches('select') && !node.dataset.customSelect) initCustomSelects(node.parentNode || document);
        else if (node.querySelector && node.querySelector('select')) initCustomSelects(node);
      }
    }
  }).observe(document.body, { childList: true, subtree: true });
})();

/* <product-form> — AJAX add to cart ----------------------------------------- */

class ProductForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    if (!this.form) return;
    this.submitButton = this.form.querySelector('[name="add"]');
    this.errorWrapper = this.querySelector('.product-form__error');
    this.form.addEventListener('submit', this.onSubmit.bind(this));
  }

  async onSubmit(event) {
    event.preventDefault();
    if (!this.submitButton || this.submitButton.hasAttribute('disabled')) return;

    // Lejáró termék: a kosárba tétel csak kifejezett tudomásulvétel után megy
    // tovább. A dátumot a Liquid teszi ki, a szöveget a fordításból vesszük.
    const expiry = this.dataset.expiryConfirm;
    if (expiry) {
      const strings = window.themeStrings || {};
      const message = String(strings.expiryMessage || '').replace('__DATE__', expiry);
      const accepted = await themeConfirm(message, {
        title: strings.expiryTitle,
        confirmLabel: strings.expiryConfirm,
        cancelLabel: strings.expiryCancel,
      });
      if (!accepted) return;
    }

    const label = this.submitButton.querySelector('.button__label') || this.submitButton;
    const originalText = label.textContent;
    this.submitButton.setAttribute('aria-busy', 'true');
    this.submitButton.classList.add('is-loading');

    const formData = new FormData(this.form);
    try {
      const response = await fetch(window.themeRoutes.cart_add_url + '.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData,
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.description || window.themeStrings.cartError);
      }
      if (this.errorWrapper) this.errorWrapper.hidden = true;
      await updateCartUI(true);
    } catch (error) {
      if (this.errorWrapper) {
        this.errorWrapper.textContent = error.message;
        this.errorWrapper.hidden = false;
      } else {
        console.error(error);
      }
    } finally {
      this.submitButton.removeAttribute('aria-busy');
      this.submitButton.classList.remove('is-loading');
      label.textContent = originalText;
    }
  }
}
customElements.define('product-form', ProductForm);

/* <read-more> — hosszú leírás összecsukása "…"-tal ------------------------- */

class ReadMore extends HTMLElement {
  connectedCallback() {
    this.content = this.querySelector('[data-read-more-content]');
    this.toggle = this.querySelector('[data-read-more-toggle]');
    if (!this.content || !this.toggle) return;

    this.label = this.toggle.querySelector('[data-read-more-label]') || this.toggle;
    this.expanded = false;
    this.render();

    this.toggle.addEventListener('click', () => {
      this.expanded = !this.expanded;
      this.render();
    });

    // Átméretezéskor és a webfontok betöltése után újra kell mérni: mindkettő
    // megváltoztatja, hogy hány sorba fér ki a szöveg.
    this.onResize = () => this.render();
    window.addEventListener('resize', this.onResize);
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => this.render());
    }
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.onResize);
  }

  render() {
    // A méréshez mindig összecsukott állapotból indulunk, különben a
    // scrollHeight és a clientHeight megegyezne.
    this.setAttribute('data-collapsed', '');
    const truncated = this.content.scrollHeight - this.content.clientHeight > 1;

    this.toggle.hidden = !truncated;
    if (this.expanded || !truncated) this.removeAttribute('data-collapsed');

    this.toggle.setAttribute('aria-expanded', this.expanded ? 'true' : 'false');
    this.label.textContent = this.expanded
      ? this.toggle.dataset.labelLess
      : this.toggle.dataset.labelMore;
  }
}
customElements.define('read-more', ReadMore);

/* Promóció kosárba tétele -------------------------------------------------
   Delegált figyelő: a promó gomb lehet a <cart-promo> szekcióban VAGY „ghost”
   sorként a kosár táblázatában (<cart-items>), ezért nem elemhez kötjük. */

function promoErrorTarget(button) {
  const promoSection = button.closest('cart-promo');
  if (promoSection) {
    const own = promoSection.querySelector('[data-promo-error]');
    if (own) return own;
  }
  return document.querySelector('[data-cart-error]');
}

async function addPromoToCart(button) {
  const variantId = button.dataset.variantId;
  if (!variantId || button.classList.contains('is-loading')) return;

  const errorTarget = promoErrorTarget(button);
  button.classList.add('is-loading');
  button.setAttribute('aria-busy', 'true');

  try {
    const response = await fetch(window.themeRoutes.cart_add_url + '.js', {
      ...fetchConfig(),
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.description || window.themeStrings.cartError);
    }
    if (errorTarget) errorTarget.hidden = true;
    // A szekció és a kosár újrarenderelődik, így a gomb „Hozzáadva” lesz.
    await updateCartUI(false);
  } catch (error) {
    if (errorTarget) {
      errorTarget.textContent = error.message || window.themeStrings.cartError;
      errorTarget.hidden = false;
    } else {
      console.error(error);
    }
  } finally {
    // Sikeres esetben a gombot már lecserélte az újrarenderelés (ilyenkor ez
    // egy leválasztott elemen fut, ártalmatlan) — de ha az elmaradna, a gomb
    // ne ragadjon be a letiltott „töltés” állapotban.
    button.classList.remove('is-loading');
    button.removeAttribute('aria-busy');
  }
}

document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  const button = target ? target.closest('[data-promo-add]') : null;
  if (button) addPromoToCart(button);
});

/* <hero-slider> — Hero diákból épített slider -------------------------------
   A léptetés natív scroll-snap görgetés, így a mobil húzás magától működik.
   Az aktuális dia a scrollLeft-ből jön, nem külön állapotból — így a kézi
   húzás és a gombos léptetés sosem csúszik szét. */

class HeroSlider extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-slider-track]');
    if (!this.track) return;

    this.slides = Array.from(this.querySelectorAll('[data-slide]'));
    this.dots = Array.from(this.querySelectorAll('[data-slider-dot]'));
    this.prevButton = this.querySelector('[data-slider-prev]');
    this.nextButton = this.querySelector('[data-slider-next]');
    if (this.slides.length < 2) return;

    this.onScroll = () => {
      window.clearTimeout(this.scrollTimer);
      this.scrollTimer = window.setTimeout(() => this.syncState(), 80);
    };
    this.track.addEventListener('scroll', this.onScroll, { passive: true });

    if (this.prevButton) this.prevButton.addEventListener('click', () => this.go(-1));
    if (this.nextButton) this.nextButton.addEventListener('click', () => this.go(1));
    this.dots.forEach((dot) => {
      dot.addEventListener('click', () => this.scrollToIndex(Number(dot.dataset.sliderDot)));
    });

    this.track.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') { event.preventDefault(); this.go(-1); }
      if (event.key === 'ArrowRight') { event.preventDefault(); this.go(1); }
    });

    // Szerkesztőben: a kiválasztott diára ugrunk, és nem léptetünk tovább.
    this.addEventListener('shopify:block:select', (event) => {
      this.stopAutoplay();
      const slide = event.target.closest('[data-slide]');
      const index = this.slides.indexOf(slide);
      if (index > -1) this.scrollToIndex(index);
    });
    this.addEventListener('shopify:block:deselect', () => this.startAutoplay());

    this.startAutoplay();
    this.syncState();
  }

  disconnectedCallback() {
    this.stopAutoplay();
    if (this.track && this.onScroll) this.track.removeEventListener('scroll', this.onScroll);
    window.clearTimeout(this.scrollTimer);
  }

  get index() {
    const width = this.track.clientWidth || 1;
    return Math.round(this.track.scrollLeft / width);
  }

  // Windowson kikapcsolt animációknál (`prefers-reduced-motion`) nem tiltjuk le
  // a léptetést — csak ugrásszerűen váltunk, animáció nélkül.
  get scrollBehavior() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
  }

  scrollToIndex(index) {
    const count = this.slides.length;
    const target = ((index % count) + count) % count;
    this.track.scrollTo({ left: target * this.track.clientWidth, behavior: this.scrollBehavior });
  }

  go(step) {
    this.scrollToIndex(this.index + step);
  }

  syncState() {
    const current = this.index;
    this.dots.forEach((dot, i) => {
      dot.setAttribute('aria-selected', i === current ? 'true' : 'false');
    });
    // A nem látható diák linkjei egyébként a fókuszsorrendben maradnának.
    // `inert` kell, nem `aria-hidden`: utóbbi fókuszálható tartalommal
    // akadálymentességi hiba lenne. Régi böngészőn egyszerűen nincs hatása.
    this.slides.forEach((slide, i) => {
      if (i === current) slide.removeAttribute('inert');
      else slide.setAttribute('inert', '');
    });
  }

  startAutoplay() {
    if (this.dataset.autoplay !== 'true') return;

    const interval = Number(this.dataset.interval) || 6000;
    const tick = () => {
      if (document.hidden) return;
      this.go(1);
    };
    this.timer = window.setInterval(tick, interval);

    // Szünet, amíg az egér vagy a fókusz a slideren van.
    const pause = () => this.stopAutoplay();
    const resume = () => {
      if (!this.timer) this.timer = window.setInterval(tick, interval);
    };
    this.addEventListener('mouseenter', pause);
    this.addEventListener('mouseleave', resume);
    this.addEventListener('focusin', pause);
    this.addEventListener('focusout', resume);
  }

  stopAutoplay() {
    if (this.timer) {
      window.clearInterval(this.timer);
      this.timer = null;
    }
  }
}
customElements.define('hero-slider', HeroSlider);

/* <hero-video> — háttérben loopoló videó -----------------------------------
   A lejátszást a `video_tag` autoplay/loop/muted attribútumai indítják; ez az
   elem csak a két megállítási szabályt kezeli, amit a szerkesztőben lehet
   kapcsolni. Ha nincs videó a szekcióban, minden hívás no-op. */

class HeroVideo extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('video');
    if (!this.video) return;

    // Autoplay-hez a némítás minden böngészőben feltétel.
    this.video.muted = true;
    this.video.setAttribute('playsinline', '');

    if (this.dataset.pauseReducedMotion === 'true') {
      this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.onMotionChange = () => this.applyState();
      this.motionQuery.addEventListener('change', this.onMotionChange);
    }

    if (this.dataset.pauseOffscreen === 'true' && 'IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(
        (entries) => {
          this.isVisible = entries[0].isIntersecting;
          this.applyState();
        },
        { threshold: 0.1 }
      );
      this.observer.observe(this);
    } else {
      this.isVisible = true;
    }

    this.applyState();
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
    if (this.motionQuery && this.onMotionChange) {
      this.motionQuery.removeEventListener('change', this.onMotionChange);
    }
  }

  get shouldPlay() {
    if (this.motionQuery && this.motionQuery.matches) return false;
    if (this.isVisible === false) return false;
    return true;
  }

  applyState() {
    if (!this.video) return;
    if (this.shouldPlay) {
      const attempt = this.video.play();
      // Az autoplay elutasítása nem hiba: a poszter marad a helyén.
      if (attempt && typeof attempt.catch === 'function') attempt.catch(() => {});
    } else {
      this.video.pause();
    }
  }
}
customElements.define('hero-video', HeroVideo);

/* <precise-search> — keresési hatókör -------------------------------------- */
/* Három mód: csak terméknév, teljes tartalom, illetve oldalak és cikkek. A
   névre szűkítés a Shopify `title:"szó"` szintaxisát építi fel, ezért a beírt
   kifejezést a beküldés pillanatában írjuk át — a mezőben a látogató a saját
   szavait látja, a találati oldalon pedig visszaalakítjuk. */
if (!customElements.get('precise-search')) {
  const TITLE_QUERY = /title:"([^"\\]*(?:\\.[^"\\]*)*)"/g;

  class PreciseSearch extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.scope = this.querySelector('[data-search-scope]');
      if (!this.form || !this.scope) return;

      // A választó csak akkor jelenik meg, ha a JS tényleg lefutott.
      this.scope.closest('label').hidden = false;

      const query = this.form.querySelector('[name="q"]');
      const original = query.value;
      if (/^title:"/.test(original)) {
        query.value = original.replace(TITLE_QUERY, '$1').replace(/ AND /g, ' ');
      } else if (original) {
        this.scope.value = this.dataset.scope || 'all';
      }

      this.onFormData = (event) => {
        const term = query.value.trim();
        if (this.scope.value === 'names' && term) {
          const tokens = term.replace(/["\\():]/g, ' ').split(/\s+/).filter(Boolean);
          event.formData.set('q', tokens.map((token) => `title:"${token}"`).join(' AND '));
          event.formData.set('type', 'product');
        } else {
          event.formData.set('q', term);
          event.formData.set('type', this.scope.value === 'pages' ? 'page,article' : 'product,page,article');
        }
      };
      this.form.addEventListener('formdata', this.onFormData);
    }

    disconnectedCallback() {
      if (this.form && this.onFormData) this.form.removeEventListener('formdata', this.onFormData);
    }
  }
  customElements.define('precise-search', PreciseSearch);
}

/* <program-finder> — digitális program téma és szint szerint ---------------- */
/* A programtermékek handle-je kiszámítható mintát követ, ezért elég a témát és
   a szintet megkérdezni, és a megfelelő termékoldalra navigálni. */
if (!customElements.get('program-finder')) {
  const PROGRAM_PREFIXES = {
    diet: 'kek-zonas-mikrobiom-dieta',
    running: 'edzesterv-futas',
    strength: 'edzesterv-sajat-testsulyos',
    breathing: 'legzesterapia',
    meditation: 'meditacio',
    cold: 'hidegterapia',
    sleep: 'alvasminoseg-javitas',
  };
  const PROGRAM_LEVELS = ['1', '2', '3', '4', '5', 'full'];

  class ProgramFinder extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.form.hidden = false;

      this.onSubmit = (event) => {
        event.preventDefault();
        const topic = this.form.elements.topic.value;
        const level = this.form.elements.level.value;
        const prefix = PROGRAM_PREFIXES[topic];
        if (!prefix || !PROGRAM_LEVELS.includes(level)) return;

        let suffix = `-${level}-het`;
        if (level === 'full') suffix = '-teljes';
        else if (topic === 'diet') suffix = `-${level}-resz`;

        const root = (this.dataset.root || '/').replace(/\/$/, '');
        window.location.assign(`${root}/products/${prefix}${suffix}`);
      };
      this.form.addEventListener('submit', this.onSubmit);
    }

    disconnectedCallback() {
      if (this.form && this.onSubmit) this.form.removeEventListener('submit', this.onSubmit);
    }
  }
  customElements.define('program-finder', ProgramFinder);
}
